/**
 * The booking flow's starting state.
 *
 * A fresh shipment already has an id and a pickup date — the next business day
 * — because the details screen quotes against them before the user has typed
 * anything. Loading capabilities start at Unknown rather than absent, so the
 * address screen can tell "not answered yet" from "answered no".
 */

import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import { v4 as uuidv4 } from "uuid";
import type {
  BookShipmentShipmentType,
  BookShipmentSlice2Type,
} from "@/Shared/Types/bookShipmentSliceTypes";
import { nextBusinessDay } from "@/Shared/Dates/nextBusinessDay";

export function bookShipmentEmptyLocationInfo(): AddLocationLocationType {
  return {
    locationType: undefined,
    distributionWarehouseBrand: undefined,
    latitudeLongitude: undefined,
    address: {},
    accessorials: [],
    notes: "",
    hours: {
      openFrom: "10:00",
      openUntil: "17:00",
    },
  };
}

function bookShipmentEmptyShipment(): BookShipmentShipmentType {
  return {
    shipmentId: uuidv4(),
    selectedQuote: undefined,
    lineItems: [],
    pickupDate: nextBusinessDay().format("YYYY-MM-DD"),
    pickupLocation: bookShipmentEmptyLocationInfo(),
    deliveryLocation: bookShipmentEmptyLocationInfo(),
    pickupContact: undefined,
    deliveryContact: undefined,
    broker: undefined,
    emergencyContactDetails: undefined,
    stripePaymentMethodId: undefined,
    isReviewingPriorToBookingShipment: false,
    pickupReferenceNumber: "",
    deliveryReferenceNumber: "",
    pickupBoothNumber: undefined,
    deliveryBoothNumber: undefined,
    finalizeBookingStages: [],
    addInsuranceToShipment: false,
    insuranceAmount: 0,
    insuranceCurrency: Currency.Cad,
    stateOfGoods: undefined,
    branchId: undefined,
    deliveryDeadline: undefined,
    pickupDeadline: undefined,
    pickupLocationHasDock: LoadingCapabilitiesAvailability.Unknown,
    pickupLocationHasForklift: LoadingCapabilitiesAvailability.Unknown,
    deliveryLocationHasDock: LoadingCapabilitiesAvailability.Unknown,
    deliveryLocationHasForklift: LoadingCapabilitiesAvailability.Unknown,
  };
}

export function initialBookShipmentSliceState(): BookShipmentSlice2Type {
  return {
    shipment: bookShipmentEmptyShipment(),
    draft: undefined,
    loadingShipment: false,
    shipmentError: undefined,
    creditCardBrand: undefined,
    enteringNewPickup: true,
    enteringNewDelivery: true,
    selectedPickupSavedLocationId: undefined,
    selectedDeliverySavedLocationId: undefined,
    isModifying: false,
    replacesId: undefined,
    isCurrentScreenDirty: false,
    completedFlowItems: [],
    makePickupLocationAFavourite: undefined,
    makeDeliveryLocationAFavourite: undefined,
  };
}
